/** Settings → Account → Devices: view/rename/remove registered devices. */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Smartphone, Laptop, Tablet, Monitor, Trash2, Pencil, Check, X } from "lucide-react";

const iconFor = (t) => {
  const s = (t || "").toLowerCase();
  if (s.includes("iphone") || s.includes("phone")) return Smartphone;
  if (s.includes("ipad") || s.includes("tablet")) return Tablet;
  if (s.includes("mac") || s.includes("windows") || s.includes("pc") || s.includes("linux")) return Laptop;
  return Monitor;
};
const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return "—"; } };

export default function DevicesSettings() {
  const [devices, setDevices] = useState(null);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState("");

  const load = async () => {
    try { setDevices((await api.get("/devices")).data.devices || []); }
    catch { setDevices([]); }
  };
  useEffect(() => { load(); }, []);

  const rename = async (id) => {
    try { await api.patch(`/devices/${id}`, { name: name.trim() }); setEditing(null); toast.success("Device renamed"); load(); }
    catch { toast.error("Could not rename"); }
  };
  const remove = async (id, current) => {
    if (current && !window.confirm("This is the device you're using now. Removing it will sign you out here. Continue?")) return;
    if (!current && !window.confirm("Remove this device? It will need to sign in again. Your inspections and data are not affected.")) return;
    try {
      await api.delete(`/devices/${id}`);
      toast.success("Device removed");
      if (current) { localStorage.removeItem("psc_token"); window.location.href = "/login"; return; }
      load();
    } catch { toast.error("Could not remove device"); }
  };

  return (
    <div className="bg-white border-2 border-slate-200 p-6 mt-6" data-testid="devices-settings-card">
      <h3 className="text-lg font-black text-slate-900">Registered Devices</h3>
      <p className="text-sm text-slate-500 mt-1">Compliance Hub remembers the devices you sign in on. Remove one to free a slot — your inspections, photos and reports are never deleted.</p>
      {devices === null ? (
        <div className="py-8 flex justify-center"><Loader2 className="animate-spin text-slate-400"/></div>
      ) : devices.length === 0 ? (
        <p className="text-sm text-slate-400 italic mt-4" data-testid="devices-empty">No registered devices.</p>
      ) : (
        <ul className="divide-y divide-slate-100 mt-4" data-testid="devices-list">
          {devices.map((d) => {
            const Icon = iconFor(d.device_type);
            return (
              <li key={d.id} className="py-3 flex items-center gap-3" data-testid={`device-row-${d.id}`}>
                <Icon size={20} className="text-slate-500 shrink-0"/>
                <div className="flex-1 min-w-0">
                  {editing === d.id ? (
                    <div className="flex items-center gap-2">
                      <input value={name} onChange={(e) => setName(e.target.value)} maxLength={60}
                        className="h-8 px-2 border-2 border-slate-300 text-sm flex-1" data-testid={`device-rename-input-${d.id}`}/>
                      <button onClick={() => rename(d.id)} className="text-emerald-600" data-testid={`device-rename-save-${d.id}`}><Check size={16}/></button>
                      <button onClick={() => setEditing(null)} className="text-slate-400"><X size={16}/></button>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-bold text-slate-900 truncate">{d.name}</span>
                        {d.current && <span className="px-1.5 py-0.5 rounded bg-[#00F2C3]/20 text-teal-700 text-[9px] font-bold uppercase" data-testid={`device-current-${d.id}`}>Current device</span>}
                      </div>
                      <p className="text-[11px] text-slate-500">{d.os} • {d.browser} · Last active {fmt(d.last_active)}</p>
                    </>
                  )}
                </div>
                {editing !== d.id && (
                  <>
                    <button onClick={() => { setEditing(d.id); setName(d.name || ""); }} className="text-slate-400 hover:text-slate-700" data-testid={`device-rename-${d.id}`}><Pencil size={15}/></button>
                    <button onClick={() => remove(d.id, d.current)} className="text-slate-400 hover:text-red-600" data-testid={`device-remove-${d.id}`}><Trash2 size={15}/></button>
                  </>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
